import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtDate, toInputDate } from '../utils';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, CartesianGrid } from 'recharts';
import api from '../api';
import { formatDistanceToNow } from 'date-fns';

const STAGE_COLORS = { Prospecting:'#8b5cf6',Qualification:'#3b82f6',Shaping:'#06b6d4','Pre-RFP':'#f59e0b',Proposal:'#f97316',Evaluation:'#ec4899',Won:'#16a34a',Lost:'#dc2626','No Bid':'#94a3b8','On Hold':'#64748b' };
const Q_COLORS = ['#3b82f6','#2563eb','#1d4ed8','#1e40af','#1e3a8a','#172554'];

function smartMoney(val) {
  const n = Number(val);
  if (!val && val !== 0 || isNaN(n)) return '—';
  if (n >= 1e9) return `$${(n/1e9).toFixed(1).replace(/\.0$/,'')}B`;
  if (n >= 1e6) return `$${(n/1e6).toFixed(1).replace(/\.0$/,'')}M`;
  if (n >= 1e3) return `$${(n/1e3).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

const QuarterTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'white',border:'1px solid var(--gray-200)',borderRadius:8,padding:'10px 14px',fontSize:13,boxShadow:'0 4px 12px rgba(0,0,0,0.1)' }}>
      <div style={{ fontWeight:700,marginBottom:4,color:'var(--gray-800)' }}>{label}</div>
      <div style={{ color:'#2563eb',fontWeight:600 }}>{smartMoney((payload[0]?.value||0)*1000)}</div>
      <div style={{ color:'var(--gray-500)',fontSize:12 }}>{payload[1]?.value||0} opportunities</div>
    </div>
  );
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/dashboard/summary').then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding:40,color:'var(--gray-400)' }}>Loading dashboard...</div>;
  if (!data) return null;

  const { kpis, oppsByStage, recentActivities, topAccounts, upcomingTasks, quarterlyPipeline } = data;

  // Pipeline by stage donut
  const stagePieData = (oppsByStage||[]).filter(s => parseInt(s.count) > 0).map(s => ({ name:s.stage, value:parseInt(s.count)||0, tcv:parseFloat(s.value)||0 }));

  // Quarterly bar chart — real data or fallback
  const quarterlyData = (() => {
    if (quarterlyPipeline && quarterlyPipeline.length > 0) {
      return quarterlyPipeline.map(q => ({
        label: `${q.quarter} ${q.year}`,
        tcv: Math.round((q.total_tcv||0)/1000),
        count: parseInt(q.count)||0
      }));
    }
    // Fallback using stage grouping
    const yr = new Date().getFullYear();
    return [
      { label:`Q1 ${yr}`, stages:['Prospecting','Qualification'] },
      { label:`Q2 ${yr}`, stages:['Shaping','Pre-RFP'] },
      { label:`Q3 ${yr}`, stages:['Proposal'] },
      { label:`Q4 ${yr}`, stages:['Evaluation','Won'] },
    ].map(g => ({
      label: g.label,
      tcv: Math.round((oppsByStage||[]).filter(s=>g.stages.includes(s.stage)).reduce((sum,s)=>sum+(s.value||0),0)/1000),
      count: (oppsByStage||[]).filter(s=>g.stages.includes(s.stage)).reduce((sum,s)=>sum+(parseInt(s.count)||0),0)
    }));
  })();

  const hasQuarterlyData = quarterlyData.some(q => q.tcv > 0);

  // 5 clickable KPI tiles
  const tiles = [
    { label:'Open Opportunities', value:kpis.totalOpportunities||0, sub:'Active pipeline', color:'blue', link:'/opportunities', icon:'💼' },
    { label:'Pipeline TCV', value:smartMoney(kpis.pipelineValue), sub:'Forecasted total', color:'green', link:'/opportunities', icon:'📊' },
    { label:'Active Contracts', value:kpis.activeContracts||0, sub:smartMoney(kpis.activeContractValue), color:'amber', link:'/contracts', icon:'📄' },
    { label:'Expiring ≤30 Days', value:kpis.expiringContracts||0, sub:'Contracts', color:kpis.expiringContracts>0?'red':'blue', link:'/contracts', icon:'⏰' },
    { label:'Open Tasks', value:kpis.totalTasks||0, sub:'Unfinished', color:kpis.totalTasks>0?'amber':'blue', link:'/tasks', icon:'✅' },
  ];

  return (
    <>
      <div className="page-header">
        <span className="page-title">Dashboard</span>
        <span style={{ fontSize:12,color:'var(--gray-400)' }}>Live · {new Date().getFullYear()}</span>
      </div>
      <div className="page-body">

        {/* 5 clickable KPI tiles */}
        <div style={{ display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:14,marginBottom:24 }}>
          {tiles.map(({ label,value,sub,color,link,icon }) => (
            <div key={label} className={`kpi-card ${color}`}
              onClick={() => navigate(link)}
              style={{ cursor:'pointer',transition:'transform 0.15s,box-shadow 0.15s',position:'relative' }}
              onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>
              <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start' }}>
                <div className="kpi-label">{label}</div>
                <span style={{ fontSize:18,opacity:0.55 }}>{icon}</span>
              </div>
              <div className="kpi-value" style={{ fontSize:24,marginTop:4 }}>{value}</div>
              <div className="kpi-sub">{sub}</div>
              <div style={{ position:'absolute',bottom:10,right:12,fontSize:11,color:'rgba(0,0,0,0.2)' }}>→</div>
            </div>
          ))}
        </div>

        {/* Charts row */}
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:20 }}>

          {/* Total Pipeline Value by Quarter — Bar chart */}
          <div className="card">
            <div className="card-header"><span className="card-title">Total Pipeline Value by Quarter</span></div>
            <div className="card-body" style={{ paddingTop:8 }}>
              {!hasQuarterlyData
                ? <div className="empty-state" style={{ padding:'28px 0' }}>
                    <div className="empty-state-icon">📊</div>
                    <div className="empty-state-text">Add close dates to opportunities to see quarterly data</div>
                  </div>
                : <ResponsiveContainer width="100%" height={210}>
                    <BarChart data={quarterlyData} margin={{ top:8,right:8,left:-5,bottom:4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" />
                      <XAxis dataKey="label" tick={{ fontSize:12 }} />
                      <YAxis tick={{ fontSize:10 }} tickFormatter={v=>`$${v}K`} />
                      <Tooltip content={<QuarterTooltip />} />
                      <Bar dataKey="tcv" name="TCV ($K)" radius={[6,6,0,0]}>
                        {quarterlyData.map((_,i) => <Cell key={i} fill={Q_COLORS[i%Q_COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
              }
            </div>
          </div>

          {/* Pipeline by Stage donut */}
          <div className="card">
            <div className="card-header"><span className="card-title">Pipeline by Stage</span></div>
            <div className="card-body" style={{ paddingTop:4 }}>
              {stagePieData.length === 0
                ? <div className="empty-state" style={{ padding:'28px 0' }}><div className="empty-state-text">No pipeline data yet</div></div>
                : <ResponsiveContainer width="100%" height={210}>
                    <PieChart>
                      <Pie data={stagePieData} cx="50%" cy="50%" innerRadius={48} outerRadius={82} dataKey="value" nameKey="name" fontSize={10}
                        label={({ name,value }) => `${name} (${value})`} labelLine>
                        {stagePieData.map((e,i) => <Cell key={i} fill={STAGE_COLORS[e.name]||'#6366f1'} />)}
                      </Pie>
                      <Tooltip formatter={(v,n,p) => [`${v} opps · ${smartMoney(p.payload.tcv)}`,p.payload.name]} />
                    </PieChart>
                  </ResponsiveContainer>
              }
            </div>
          </div>
        </div>

        {/* Bottom row */}
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 300px',gap:20 }}>

          {/* Recent Activity */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Recent Activity</span>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/activities')}>View all</button>
            </div>
            <div className="card-body" style={{ paddingTop:4 }}>
              {(recentActivities||[]).length===0 && <div className="empty-state"><div className="empty-state-text">No activity yet</div></div>}
              {(recentActivities||[]).map(a => (
                <div className="activity-item" key={a.id}>
                  <div className="activity-avatar">{(a.user_name||'U').charAt(0).toUpperCase()}</div>
                  <div className="activity-content">
                    <div className="activity-subject">{a.subject}</div>
                    {a.body && <div className="activity-body">{a.body}</div>}
                    <div className="activity-meta">
                      {a.user_name} · {formatDistanceToNow(new Date(a.created_at),{addSuffix:true})}
                      <span style={{ marginLeft:6,color:'var(--gray-400)',fontSize:11 }}>{String(a.created_at).substring(0,4)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming Tasks — due within 5 days */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Due Within 5 Days</span>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/tasks')}>All tasks</button>
            </div>
            <div className="card-body" style={{ paddingTop:4 }}>
              {(upcomingTasks||[]).length===0 && (
                <div className="empty-state">
                  <div className="empty-state-icon">✅</div>
                  <div className="empty-state-text">Nothing due in the next 5 days</div>
                </div>
              )}
              {(upcomingTasks||[]).map(t => {
                const isOverdue = t.due_date && new Date(toInputDate(t.due_date)) < new Date(new Date().toISOString().split('T')[0]);
                const daysLeft = t.due_date ? Math.ceil((new Date(toInputDate(t.due_date))-new Date(new Date().toISOString().split('T')[0]))/(1000*60*60*24)) : null;
                return (
                  <div key={t.id} onClick={() => navigate('/tasks')} style={{ padding:'9px 0',borderBottom:'1px solid var(--gray-100)',cursor:'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.opacity='0.75'} onMouseLeave={e => e.currentTarget.style.opacity='1'}>
                    <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start' }}>
                      <div style={{ fontSize:13,fontWeight:500,flex:1 }}>{t.title}</div>
                      <span style={{ fontSize:11,fontWeight:700,padding:'2px 7px',borderRadius:10,background:isOverdue?'#fee2e2':daysLeft<=1?'#ffedd5':'#fef3c7',color:isOverdue?'#dc2626':daysLeft<=1?'#c2410c':'#92400e',marginLeft:8,flexShrink:0 }}>
                        {isOverdue ? 'Overdue' : daysLeft===0 ? 'Today' : `${daysLeft}d`}
                      </span>
                    </div>
                    <div style={{ fontSize:12,color:'var(--gray-500)',marginTop:2 }}>
                      {t.assigned_to_name && <span>👤 {t.assigned_to_name}</span>}
                      {t.due_date && <span style={{ marginLeft:t.assigned_to_name?8:0 }}>📅 {fmtDate(t.due_date)}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Accounts */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Top Accounts</span>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/accounts')}>All</button>
            </div>
            {(topAccounts||[]).map((acc,i) => (
              <div key={acc.id} onClick={() => navigate(`/accounts/${acc.id}`)}
                style={{ display:'flex',alignItems:'center',gap:10,padding:'10px 16px',borderBottom:'1px solid var(--gray-100)',cursor:'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background='var(--gray-50)'}
                onMouseLeave={e => e.currentTarget.style.background=''}>
                <div style={{ width:22,height:22,background:'var(--primary-light)',color:'var(--primary)',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700 }}>{i+1}</div>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ fontSize:13,fontWeight:500,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{acc.name}</div>
                  <div style={{ fontSize:11,color:'var(--gray-400)' }}>{acc.customer_type} · {smartMoney(acc.pipeline)}</div>
                </div>
              </div>
            ))}
            {(topAccounts||[]).length===0 && <div className="empty-state" style={{ padding:20 }}><div className="empty-state-text">No accounts yet</div></div>}
          </div>
        </div>
      </div>
    </>
  );
}
